import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActionType,
  convertToISO,
  DayIntervalUnit,
  parseMMDDYYYYToDate,
  RoutineType,
  RoutineWorkflow,
  TriggerType,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { router } from 'expo-router';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { number, string, object as yupObject } from 'yup';
import { Colors } from '../../../themes/colors';
import { ControlledSelect, FormContext } from '../../forms';
import { KeyboardSafeView } from '../../keyboard/keyboardSafeView';
import { TopModalBar } from '../../modals/topModalBar';
import { TopComponentBar } from '../../navigation';
import { TouchableButton } from '../../touchableButton';
import AlertLogicPicker from './alertLogicPicker';
import { AlertInputs, AlertType } from './types';

type AlertFormProps = {
  defaultValues: any;
  routine?: WorkspaceRoutine;
  workspaceAsset?: WorkspaceAsset;
  product?: { id: string; workspace?: { id: string } };
  group?: { id: string; workspace?: { id: string } };
  counterRoutines: WorkspaceRoutine[];
  availableRoutines?: WorkspaceRoutine[];
  workflowProps?: RoutineWorkflow;
  isEdit?: boolean;
  headerTitle?: string;
  backButtonLabel?: string;
  openDropdownOnLoad?: boolean;
  submitAlert: (input: any) => Promise<void>;
  deleteAlert?: () => Promise<void>;
  onClose?: () => void;
};

export const AlertForm = ({
  defaultValues,
  routine,
  workspaceAsset,
  product,
  group,
  counterRoutines,
  availableRoutines,
  workflowProps,
  isEdit = false,
  headerTitle,
  backButtonLabel,
  openDropdownOnLoad,
  submitAlert,
  deleteAlert,
  onClose,
}: AlertFormProps) => {
  const { t } = useTranslation();
  const [additionalLogic, setAdditionalLogic] = useState(false);
  const [showRoutinesDropdown, setShowRoutinesDropdown] = useState(false);
  const workspaceId = workspaceAsset?.workspace?.id || product?.workspace?.id || group?.workspace?.id;
  const workspaceAssetId = workspaceAsset?.id;
  const productId = product?.id;
  const groupId = group?.id;
  const MIN_REMINDER_MINUTES = 15;

  const isValidDate = (value?: string) => {
    if (!value) return true;
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!regex.test(value)) return false;
    const [month, day, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const formSchema = yupObject({
    selectedRoutine: string().optional(),
    alertType: string()
      .required()
      .min(1, t('alerts.validation.alertTypeRequired')),
    inputField: string()
      .required()
      .min(1, t('alerts.validation.inputRequired')),
    secondAlertType: string().optional(),
    secondInputField: string()
      .required()
      .when('secondAlertType', {
        is: (val: any) => val !== undefined,
        otherwise: (schema) => schema.optional(),
      }),
    alertLabel: string().optional(),
    alertLabelPlaceholder: string().optional(),
    startDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    endDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    minValue: number().optional(),
    maxValue: number().optional(),
    days: number().optional(),
    daysStartDate: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (val: string) =>
          val === AlertType.Days || secondAlertType === AlertType.Days || val === AlertType.RecurringInterval || secondAlertType === AlertType.RecurringInterval,
        then: (schema) =>
          schema.required(t('alerts.validation.startDateRequiredForDays')),
        otherwise: (schema) => schema.optional(),
      }),
    completions: number().optional(),
    daysSkipped: number().optional(),
    onDate: string().optional(),
    dayInterval: string()
      .when(['alertType', 'dayIntervalUnit'], {
        is: (alertType: string, dayIntervalUnit: string) => {
          return (
            alertType === AlertType.RecurringInterval &&
            dayIntervalUnit === DayIntervalUnit.Minutes
          );
        },
        then: (schema) => schema
          .required('Interval is required')
          .test('min-minutes', 'Must be at least 15 minutes', (value) => {
            const numValue = parseInt(value || '0', 10);
            return numValue >= MIN_REMINDER_MINUTES;
          }),
        otherwise: (schema) => schema.when('alertType', {
          is: (val: string) => val === AlertType.RecurringInterval,
          then: (schema) => schema
            .required('Interval is required')
            .test('min-interval', 'Must be at least 1', (value) => {
              const numValue = parseInt(value || '0', 10);
              return numValue >= 1;
            }),
          otherwise: (schema) => schema.optional(),
        }),
      }),
    dayIntervalUnit: string()
      .when('alertType', {
        is: (val: string) => val === AlertType.RecurringInterval,
        then: (schema) => schema.required('Interval unit is required'),
        otherwise: (schema) => schema.optional(),
      }),
    onInterval: number().optional(),
    onIntervalStart: number().optional(),
    intervalRoutineId: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (val: string) =>
          val === AlertType.Interval || secondAlertType === AlertType.Interval,
        then: (schema) =>
          schema.required(t('alerts.validation.routineIdRequiredForInterval')),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, isSubmitting },
    setValue,
    getValues,
  } = formMethods;

  const alertType = useWatch({ control, name: 'alertType' });
  const selectedRoutineId = useWatch({ control, name: 'selectedRoutine' });
  
  // Use selected routine from form, fallback to passed routine
  const currentRoutine = availableRoutines && selectedRoutineId 
    ? availableRoutines.find(r => r.id === selectedRoutineId) || routine
    : routine;
    
  const workspaceRoutineId = currentRoutine?.id;
  const routineType = currentRoutine?.type;
  const routineName = currentRoutine?.config?.routineButtonText;
  const defaultAlertName = `${t('alerts.alert')}: ${routineName}`;
  const backButtonLabelText = backButtonLabel
    ? t(backButtonLabel)
    : t('topNav.cancel');

  const isCounterRoutine = useMemo(() => {
    return routineType === RoutineType.Counter;
  }, [routineType]);

  const secondAlertType = useWatch({ control, name: 'secondAlertType' });
  const secondInputField = useWatch({ control, name: 'secondInputField' });
  const alertName = useWatch({ control, name: AlertInputs.alertLabel });
  const alertNamePlaceholder = useWatch({
    control,
    name: AlertInputs.alertLabelPlaceholder,
  });

  const fromDate = useWatch({ control, name: AlertInputs.startDate });
  const toDate = useWatch({ control, name: AlertInputs.endDate });
  const minCounterValue = useWatch({ control, name: AlertInputs.minValue });
  const maxCounterValue = useWatch({ control, name: AlertInputs.maxValue });
  const daysCounterValue = useWatch({ control, name: AlertInputs.days });
  const daysStartDate = useWatch({ control, name: AlertInputs.daysStartDate });
  const completionsValue = useWatch({ control, name: AlertInputs.completions });
  const daysSkippedValue = useWatch({ control, name: AlertInputs.daysSkipped });
  const onDateValue = useWatch({ control, name: AlertInputs.onDate });
  const dayIntervalValue = useWatch({ control, name: AlertInputs.dayInterval });
  const dayIntervalUnitValue = useWatch({ control, name: AlertInputs.dayIntervalUnit });

  const intervalRoutineIdValue = useWatch({
    control,
    name: AlertInputs.intervalRoutineId,
  });
  const onIntervalValue = useWatch({ control, name: AlertInputs.onInterval });
  const onIntervalStart = useWatch({
    control,
    name: AlertInputs.onIntervalStart,
  });

  const getAlertName = useCallback((alertType: AlertType, isSecondAlert: boolean) => {
    let alertValue;

    switch (alertType) {
      case AlertType.Date:
        alertValue = getValues(AlertInputs.onDate);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${t('alerts.on')} ${alertValue}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${t('alerts.on')} ${alertValue}`
          : `${defaultAlertName}`;
      case AlertType.DaysSkipped:
        alertValue = getValues(AlertInputs.daysSkipped);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.days')} ${t(
                'alerts.skipped'
              )}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${alertValue} ${t('alerts.days')} ${t(
              'alerts.skipped'
            )}`
          : `${defaultAlertName}`;
      case AlertType.RoutineCompletions:
        alertValue = getValues(AlertInputs.completions);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.completions')}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${alertValue} ${t('alerts.completions')}`
          : `${defaultAlertName}`;

      case AlertType.Days:
        alertValue = getValues(AlertInputs.days);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.days')}`
            : ``;
        }
        return alertValue
          ? `${defaultAlertName}, ${t('alerts.every')} ${alertValue} ${t(
              'alerts.days'
            )}`
          : `${defaultAlertName}`;

      case AlertType.Min:
        alertValue = getValues(AlertInputs.minValue);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${t('alerts.abovePlaceholder')} ${alertValue}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${t('alerts.abovePlaceholder')} ${alertValue}`
          : `${defaultAlertName}`;

      case AlertType.Max:
        alertValue = getValues(AlertInputs.maxValue);
        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${t('alerts.belowPlaceholder')} ${alertValue}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${t('alerts.belowPlaceholder')} ${alertValue}`
          : `${defaultAlertName}`;

      case AlertType.Interval: {
        alertValue = getValues(AlertInputs.onInterval);
        const routineId = getValues(AlertInputs.intervalRoutineId);
        const uom = routineId ? counterRoutines.find((r) => r.id === routineId)?.config.uom : '';

        if (isSecondAlert) {
          return alertValue
            ? ` ${t('alerts.or')} ${t('alerts.every')} ${alertValue} ${uom}`
            : '';
        }
        return alertValue
          ? `${defaultAlertName}, ${t('alerts.every')} ${alertValue} ${uom}`
          : `${defaultAlertName}`;
      }
      case AlertType.RecurringInterval: {
        const intervalValue = getValues(AlertInputs.dayInterval);
        const unitValue = getValues(AlertInputs.dayIntervalUnit);
        const unitLabel = unitValue ? t(`alerts.${unitValue.toLowerCase()}`, unitValue.toLowerCase()) : '';
        
        if (isSecondAlert) {
          return intervalValue && unitValue
            ? ` ${t('alerts.or')} ${t('alerts.every')} ${intervalValue} ${unitLabel}`
            : '';
        }
        return intervalValue && unitValue
          ? `${defaultAlertName}, ${t('alerts.every')} ${intervalValue} ${unitLabel}`
          : `${defaultAlertName}`;
      }
      default:
        return `${defaultAlertName}`;
    }
  }, [counterRoutines, defaultAlertName, getValues, t]);

  const updateFormInputField = useCallback((inputName: string, alertType: AlertType) => {
    switch (alertType) {
      case AlertType.Date:
        setValue(inputName, onDateValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.DaysSkipped:
        setValue(inputName, daysSkippedValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.RoutineCompletions:
        setValue(inputName, completionsValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.Days:
        setValue(inputName, daysCounterValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.Min:
        setValue(inputName, minCounterValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.Max:
        setValue(inputName, maxCounterValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.Interval:
        setValue(AlertInputs.intervalRoutineId, intervalRoutineIdValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setShowRoutinesDropdown(true);
        setValue(inputName, onIntervalValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;
      case AlertType.RecurringInterval:
        setValue(AlertInputs.dayInterval, dayIntervalValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(AlertInputs.dayIntervalUnit, dayIntervalUnitValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(AlertInputs.daysStartDate, daysStartDate, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(inputName, dayIntervalValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        break;

      default:
        break;
    }
  }, [
    completionsValue,
    daysCounterValue,
    daysSkippedValue,
    daysStartDate,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    maxCounterValue,
    minCounterValue,
    onDateValue,
    onIntervalValue,
    setValue
  ]);

  const counterRoutineItems = useMemo(() => {
    if (!counterRoutines || !counterRoutines.length) return [];

    return counterRoutines.map((routine) => {
      const { config, id } = routine;

      return {
        label: `${t('alerts.Every')} ___ ${config.uom}`,
        value: id,
        count: undefined, // Counter routines don't have state.count, might come from config
        type: AlertInputs.onIntervalStart,
        uom: config.uom,
      };
    });
  }, [
    counterRoutines, 
    t
  ]);

  // update first inputField
  useEffect(() => {
    if (!alertType) return;

    updateFormInputField('inputField', alertType);

    // update custom label input
    const updatedName = getAlertName(alertType, false);
    setValue(AlertInputs.alertLabelPlaceholder, updatedName, {
      shouldDirty: false,
    });
  }, [
    alertType, 
    minCounterValue, 
    maxCounterValue, 
    daysCounterValue, 
    daysStartDate,
    completionsValue, 
    daysSkippedValue, 
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue, 
    onDateValue, 
    onIntervalValue, 
    updateFormInputField, 
    getAlertName, 
    setValue
  ]);

  // update second inputField
  useEffect(() => {
    if (!secondAlertType) return;
    updateFormInputField('secondInputField', secondAlertType);

    // update placeholder label
    const currentPlaceholder = getValues(AlertInputs.alertLabelPlaceholder);
    const strToAppend = getAlertName(secondAlertType, true);

    setValue(
      AlertInputs.alertLabelPlaceholder,
      currentPlaceholder + strToAppend,
      { shouldDirty: false }
    );
  }, [
    secondAlertType,
    minCounterValue,
    maxCounterValue,
    daysCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    onDateValue,
    onIntervalValue,
    updateFormInputField, 
    getValues, 
    getAlertName, 
    setValue
  ]);

  // reset form on render and every time a user selects from drop picker
  useEffect(() => {
    // hide routines interval dropdown
    if (alertType !== AlertType.Interval) {
      setShowRoutinesDropdown(false);
    }

    if (isEdit) {
      const defaultAlertType = defaultValues.alertType;

      if (alertType !== defaultAlertType) {
        reset({
          ...getValues(), // leave out secondAlertType
          secondInputField: secondInputField || defaultValues.secondInputField,
          [AlertInputs.onDate]: undefined,
          [AlertInputs.daysSkipped]: undefined,
          [AlertInputs.completions]: undefined,
          [AlertInputs.days]: undefined,
          [AlertInputs.daysStartDate]: defaultValues.daysStartDate,
          [AlertInputs.maxValue]: undefined,
          [AlertInputs.minValue]: undefined,
          [AlertInputs.alertLabel]: '',
          [AlertInputs.alertLabelPlaceholder]: defaultAlertName,
          [AlertInputs.startDate]: undefined,
          [AlertInputs.endDate]: undefined,
          [AlertInputs.onInterval]: undefined,
          [AlertInputs.intervalRoutineId]: undefined,
          [AlertInputs.onIntervalStart]: undefined,
          [AlertInputs.dayInterval]: undefined,
          [AlertInputs.dayIntervalUnit]: undefined,
        });
      }

      reset({
        ...getValues(),
        [AlertInputs.alertLabelPlaceholder]: defaultAlertName,
      });
      return;
    }

    reset({
      ...getValues(), // leave out alertType
      inputField: undefined,
      [AlertInputs.onDate]: undefined,
      [AlertInputs.daysSkipped]: undefined,
      [AlertInputs.completions]: undefined,
      [AlertInputs.days]: undefined,
      [AlertInputs.daysStartDate]: defaultValues.daysStartDate,
      [AlertInputs.maxValue]: undefined,
      [AlertInputs.minValue]: undefined,
      [AlertInputs.alertLabel]: '',
      [AlertInputs.alertLabelPlaceholder]: defaultAlertName,
      [AlertInputs.startDate]: undefined,
      [AlertInputs.endDate]: undefined,
      [AlertInputs.onInterval]: undefined,
      [AlertInputs.intervalRoutineId]: undefined,
      [AlertInputs.onIntervalStart]: undefined,
      [AlertInputs.dayInterval]: undefined,
      [AlertInputs.dayIntervalUnit]: undefined,
    });
  }, [
    alertType, 
    isEdit, 
    defaultValues, 
    reset, 
    getValues, 
    defaultAlertName, 
    secondInputField
  ]);

  const defaultRoutineConfig = useMemo(() => {
    const config = {
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      every: daysCounterValue,
      onInterval: onIntervalValue,
      onIntervalStart: onIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      startDate: (daysCounterValue || dayIntervalValue)
        ? parseMMDDYYYYToDate(daysStartDate)
        : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval: dayIntervalValue ? parseInt(dayIntervalValue, 10) : undefined,
      dayIntervalUnit: dayIntervalUnitValue,
      routineType,
    };
    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    workspaceRoutineId,
    routineType,
    completionsValue,
    daysSkippedValue,
    daysCounterValue,
    daysStartDate,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    onIntervalStart,
    intervalRoutineIdValue,
  ]);

  const counterRoutineConfig = useMemo(() => {
    const config = {
      onInterval: onIntervalValue,
      onIntervalStart: onIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      ceiling: minCounterValue,
      floor: maxCounterValue,
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      every: daysCounterValue,
      startDate: (daysCounterValue || dayIntervalValue)
        ? parseMMDDYYYYToDate(daysStartDate)
        : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval: dayIntervalValue ? parseInt(dayIntervalValue, 10) : undefined,
      dayIntervalUnit: dayIntervalUnitValue,
      routineType,
    };

    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    routineType,
    workspaceRoutineId,
    minCounterValue,
    maxCounterValue,
    daysCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    onIntervalStart,
    intervalRoutineIdValue,
  ]);

  const onSubmit = async (data: any) => {
    const name = alertName || alertNamePlaceholder;
    const workspace = { id: workspaceId };
    const workspaceAsset = { id: workspaceAssetId };
    const workflowId = workflowProps?.id;
    const trigger = {
      type: TriggerType.Routine,
      config: counterRoutineConfig
        ? counterRoutineConfig
        : defaultRoutineConfig,
    };

    if (trigger.config.dayIntervalUnit && trigger.config.startDate) {
      const now = moment();
      const startDate = moment(trigger.config.startDate);
      
      // Preserve the date but include current time information
      let adjustedStartDate = startDate;

      switch (trigger.config.dayIntervalUnit) {
        case DayIntervalUnit.Minutes:
        case DayIntervalUnit.Hours:
          adjustedStartDate = startDate
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second());
          break;
      }
      
      trigger.config.startDate = adjustedStartDate.toISOString();
    }

    const action = {
      type: ActionType.Alert,
      config: { promptWorkspaceRoutineId: workspaceRoutineId },
    };
    const disabledBetween =
      fromDate && toDate
        ? { start: convertToISO(fromDate), end: convertToISO(toDate) }
        : undefined;

    const newAlertInput = {
      name: name,
      workspace: workspace,
      ...(workspaceAssetId && { workspaceAsset: workspaceAsset }),
      ...(productId && { product: { id: productId } }),
      ...(groupId && { group: { id: groupId } }),
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    const editAlertInput = {
      id: workflowId,
      name: name,
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    await submitAlert(isEdit ? editAlertInput : newAlertInput);
    reset();
  };

  const isWeb = Platform.OS === 'web';

  const backButton = (
    <TouchableButton
      onPress={() => {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.replace('/');
        }
      }}
      icon="chevron-left"
      label={t('common.back')}
      iconSize={28}
    />
  );

  return (
    <FormContext.Provider value={formMethods}>
      {isWeb ? (
        <TopModalBar
          title={t(`${headerTitle}`)}
          onCancel={onClose}
          showSave={true}
          onRightPress={handleSubmit(onSubmit)}
          rightPressDisabled={isSubmitting || !(isDirty && isValid)}
          backButtonLabel={backButtonLabelText}
        />
      ) : (
        <TopComponentBar
          title={isEdit ? t('alerts.editAlert') : t('alerts.newAlert')}
          confirmBack={false}
          showSave={true}
          rightPressDisabled={isSubmitting || !(isDirty && isValid)}
          onRightPress={handleSubmit(onSubmit)}
          backButton={backButton}
        />
      )}
      <KeyboardSafeView style={styles.container}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scrollViewContainer}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled={true}
        >
          {availableRoutines && availableRoutines.length > 0 && (
            <View style={styles.routineSelectionContainer}>
              <Text style={styles.routineSelectionLabel}>
                {t('alerts.selectRoutineToPrompt')}
              </Text>
              <ControlledSelect
                name="selectedRoutine"
                control={control}
                label={t('alerts.selectRoutineToPrompt')}
                items={availableRoutines.map(routine => ({
                  label: routine.name || routine.config?.routineButtonText || 'Unnamed Routine',
                  value: routine.id
                }))}
                onSelectItem={(item: string | null) => {
                  if (item && typeof item === 'string') {
                    setValue('selectedRoutine', item, { shouldDirty: true, shouldValidate: true });
                  }
                }}
                dropDownPickerProps={{
                  ArrowDownIconComponent: () => <MaterialCommunityIcons name="chevron-down" size={28} />,
                  ArrowUpIconComponent: () => <MaterialCommunityIcons name="chevron-up" size={28} />,
                  style: styles.routineDropdown,
                  placeholderStyle: styles.routinePlaceholderLabel,
                }}
              />
            </View>
          )}
          {counterRoutineItems && (
            <AlertLogicPicker
              control={control}
              alertType={alertType}
              secondAlertType={secondAlertType}
              additionalLogic={additionalLogic}
              isCounterRoutine={isCounterRoutine}
              setAdditionalLogic={(value) => setAdditionalLogic(value)}
              showRoutinesDropdown={showRoutinesDropdown}
              setShowRoutinesDropdown={(value) =>
                setShowRoutinesDropdown(value)
              }
              counterRoutineItems={counterRoutineItems}
              openDropdownOnLoad={openDropdownOnLoad}
            />
          )}
          {isEdit && (
            <Pressable
              onPress={deleteAlert}
              hitSlop={5}
              style={styles.deleteAlertButton}
            >
              <Text allowFontScaling={false} style={styles.deleteLabel}>
                {t('alerts.deleteAlert')}
              </Text>
            </Pressable>
          )}
        </ScrollView>
      </KeyboardSafeView>
    </FormContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: Colors.primaryGrayBackground,
    padding: 20,
  },
  scrollViewContainer: {
    flexGrow: 1,
    paddingBottom: 400, // Increased padding to ensure date picker doesn't hide fields
  },
  routineSelectionContainer: {
    marginBottom: 20,
    backgroundColor: Colors.white,
    borderRadius: 8,
    padding: 16,
    zIndex: 1000,
    elevation: 1000,
  },
  routineSelectionLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.black,
    marginBottom: 8,
  },
  routineDropdown: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    fontSize: 16,
    fontWeight: '400',
  },
  routinePlaceholderLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayFill,
  },
  deleteAlertButton: {
    width: 100,
    height: 25,
    marginTop: 20,
  },
  deleteLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.red,
    letterSpacing: -0.25,
  },
});
